import { Metadata } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/entity_pb';
import { GetExperimentRequest } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/experiment_service_pb';
import { KeySelection } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/listing_pb';
import { ExperimentService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/experiment_service_connect';
import { createClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { IStyle, MessageBarType, Toggle } from '@fluentui/react';
import {
  Button,
  buttonStylesStealth,
  ClassNamesFromIStyles,
  Dropdown,
  IH2OTheme,
  KeyValuePairEditor,
  Search,
  TextField,
  useClassNames,
  useTheme,
  useToast,
} from '@h2oai/ui-kit';
import React from 'react';

import {
  BaselineAggregation,
  CategoricalAggregate,
  NumericalAggregate,
} from '../../mlops/gen/ai/h2o/mlops/monitoring/v2/aggregate_pb';
import { LogicalType } from '../../mlops/gen/ai/h2o/mlops/monitoring/v2/logical_type_pb';
import { Column, MonitoringOptions } from '../../mlops/gen/ai/h2o/mlops/monitoring/v2/monitoring_options_pb';
import { useCloudPlatformDiscovery } from '../../utils/hooks';
import { formatError } from '../../utils/utils';
import { OneOf } from '../Orchestrator/WorkflowTabCanvas';
import { ENDPOINTS } from './constants';
import { getParsedJson, snakeToText } from './ModelMetadataView';
import { TooltipLabel } from './ModelSelector';

// TODO: Enable when the support for updating monitored columns is added on backend after MLOps 1.0 release. Only non-monitored columns should be updatable.
export const COLUMN_UPDATE_SUPPORTED = false;

export type BaselineAggregationFixed = BaselineAggregation &
  OneOf<{
    categoricalAggregate?: CategoricalAggregate;
    numericalAggregate?: NumericalAggregate;
  }>;
type MonitoringOptionsColumnsWithProps = Omit<MonitoringOptions, 'columns'> & {
  columns: ColumnWithProps[];
};
export type MonitoringOptionsFixed = Omit<MonitoringOptionsColumnsWithProps, 'baselineAggregations'> & {
  baselineAggregations: BaselineAggregationFixed[];
};

type MonitoringProps = {
  monitoringOptions?: MonitoringOptionsFixed;
  onChangeMonitoringOptions: (options: MonitoringOptionsFixed) => void;
  experimentId: string;
  isExistingMonitoring: boolean;
  shouldFetchMetadata: boolean;
  disabled?: boolean;
  nonUpdatableColumns?: string[]; // Column cannot be updated if already monitored.
};
type LogicalTypeDropdownOption = {
  key: string;
  text: string;
  value: LogicalType;
};
type ColumnWithProps = Column & {
  isColumnDisabled?: boolean;
};

interface IModelMetadataStyles {
  schemaTable: IStyle;
  schemaTitle: IStyle;
  tableScrollWrapper: IStyle;
  tableHeaderSticky: IStyle;
}
export const modelMetadataStyles = (theme: IH2OTheme): Partial<IModelMetadataStyles> => ({
  schemaTable: {
    width: '100%',
    borderCollapse: 'collapse',
    '& th, & td': {
      border: `1px solid ${theme.semanticColors?.inputBorder}`,
      padding: 8,
      textAlign: 'left',
    },
    '& th': {
      // TODO: Theme this.
      backgroundColor: '#f4f4f4',
    },
    '& tr:hover': {
      // TODO: Theme this.
      backgroundColor: '#f9f9f9',
    },
  },
  schemaTitle: {
    '&:first-letter': {
      textTransform: 'uppercase',
    },
  },
  tableScrollWrapper: {
    maxHeight: 485,
    overflowY: 'auto',
    display: 'block',
    marginBottom: 20,
  },
  tableHeaderSticky: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
});

// Map common field types to logical types.
const typeToLogicalTypeMap: Record<string, LogicalType> = {
    // Numerical
    FLOAT64: LogicalType.NUMERICAL,
    FLOAT32: LogicalType.NUMERICAL,
    FLOAT: LogicalType.NUMERICAL,
    DOUBLE: LogicalType.NUMERICAL,
    INT64: LogicalType.NUMERICAL,
    INT32: LogicalType.NUMERICAL,
    INT: LogicalType.NUMERICAL,
    NUMBER: LogicalType.NUMERICAL,
    DECIMAL: LogicalType.NUMERICAL,

    // Categorical
    STR: LogicalType.CATEGORICAL,
    STRING: LogicalType.CATEGORICAL,
    CHAR: LogicalType.CATEGORICAL,
    VARCHAR: LogicalType.CATEGORICAL,
    BOOLEAN: LogicalType.CATEGORICAL, // Optional: Could also go to LOGICAL_TYPE_UNSPECIFIED.

    // Datetime
    DATE: LogicalType.DATETIME,

    // Timestamp
    TIMESTAMP: LogicalType.TIMESTAMP,
    DATETIME: LogicalType.TIMESTAMP, // Sometimes used interchangeably.

    // Text
    TEXT: LogicalType.TEXT,
    LONGTEXT: LogicalType.TEXT,
    PARAGRAPH: LogicalType.TEXT,

    // Image
    IMAGE: LogicalType.IMAGE,
    BLOB: LogicalType.IMAGE, // Optional depending on use case.

    // ID
    UUID: LogicalType.ID,
    ID: LogicalType.ID,
  },
  // Used only in special case when schema output columns are computed from columns of actual deployment.
  logicalTypeToTypeMap: Record<LogicalType, string> = {
    [LogicalType.LOGICAL_TYPE_UNSPECIFIED]: 'UNSPECIFIED',
    [LogicalType.NUMERICAL]: 'NUMBER',
    [LogicalType.CATEGORICAL]: 'STRING',
    [LogicalType.DATETIME]: 'DATE',
    [LogicalType.TIMESTAMP]: 'TIMESTAMP',
    [LogicalType.TEXT]: 'TEXT',
    [LogicalType.IMAGE]: 'IMAGE',
    [LogicalType.ID]: 'ID',
  },
  // Dropdown options based on type LogicalType
  logicalTypeDropdownOptions: LogicalTypeDropdownOption[] = Object.entries(LogicalType)?.map(([key, value]) => ({
    key,
    text: snakeToText(value),
    value,
  })),
  numericalAggregateToConfig = (aggregate?: NumericalAggregate) => ({
    binEdges: aggregate?.binEdges ? `[${aggregate.binEdges.join(',')}]` : '',
    binCount: aggregate?.binCount ? `[${aggregate.binCount.join(',')}]` : '',
    mean: aggregate?.mean ? `${aggregate?.mean}` : '',
    standardDeviation: aggregate?.standardDeviation ? `${aggregate?.standardDeviation}` : '',
    min: aggregate?.min ? `${aggregate?.min}` : '',
    max: aggregate?.max ? `${aggregate?.max}` : '',
    sum: aggregate?.sum ? `${aggregate?.sum}` : '',
  }),
  categoricalAggregateToConfig = (aggregate?: CategoricalAggregate) => {
    const config: Record<string, string> = {};
    Object.entries(aggregate?.valueCounts || {}).forEach(([key, value]) => {
      config[key] = value ? `${value}` : '';
    });
    return config;
  },
  configToNumericalAggregate = (config: Record<string, string>): NumericalAggregate => {
    const numericalAggregate = {
      binEdges: [],
      binCount: [],
      mean: 0,
      standardDeviation: 0,
      min: 0,
      max: 0,
      sum: 0,
    };
    if (!config) return numericalAggregate;
    numericalAggregate.binEdges = config.binEdges ? JSON.parse(config.binEdges) : [];
    numericalAggregate.binCount = config.binCount ? JSON.parse(config.binCount) : [];
    numericalAggregate.mean = parseFloat(config.mean);
    numericalAggregate.standardDeviation = parseFloat(config.standardDeviation);
    numericalAggregate.min = parseFloat(config.min);
    numericalAggregate.max = parseFloat(config.max);
    numericalAggregate.sum = parseFloat(config.sum);
    return numericalAggregate;
  },
  configToCategoricalAggregate = (config: Record<string, string>): CategoricalAggregate => {
    const categoricalAggregate = {
      valueCounts: {},
    };
    if (!config) return categoricalAggregate;
    const valueCounts: Record<string, number> = {};
    Object.entries(config).forEach(([key, value]) => {
      valueCounts[key] = parseInt(value, 10);
    });
    categoricalAggregate.valueCounts = valueCounts;
    return categoricalAggregate;
  };

const ValueEditor = ({
  onChangeMonitoringOptions,
  monitoringOptions,
  baselineAggregationColumnName,
  readOnly,
}: {
  onChangeMonitoringOptions: (options: MonitoringOptionsFixed) => void;
  monitoringOptions: MonitoringOptionsFixed;
  baselineAggregationColumnName: string;
  readOnly?: boolean;
}) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const baselineAggregation = monitoringOptions?.baselineAggregations?.find(
    (aggregation) => aggregation.column === baselineAggregationColumnName
  );
  return (
    <>
      <Button
        iconName="ChevronRight"
        text={readOnly ? 'View' : 'Edit'}
        onClick={(ev) => {
          const icon = (ev.currentTarget as HTMLElement)?.querySelector('.ms-Icon');
          if (icon) {
            setIsEditing(!icon.classList?.contains('rotate90'));
            icon.classList.toggle('rotate90');
          }
        }}
        styles={[
          buttonStylesStealth,
          {
            icon: {
              fontSize: 14,
              transitionDuration: '300ms',
              transitionProperty: 'transform',
            },
            root: {
              '.ms-Icon.rotate90': {
                transform: 'rotate(90deg)',
              },
              backgroundColor: 'transparent',
            },
          },
        ]}
      />
      {isEditing && (
        <KeyValuePairEditor
          config={
            baselineAggregation?.logicalType === LogicalType.NUMERICAL
              ? numericalAggregateToConfig(baselineAggregation?.numericalAggregate)
              : categoricalAggregateToConfig(baselineAggregation?.categoricalAggregate)
          }
          onUpdateConfig={(newConfig: Record<string, string>) => {
            if (baselineAggregation?.logicalType === LogicalType.NUMERICAL) {
              const newBaselineAggregations = monitoringOptions?.baselineAggregations;
              const newBaselineIdx = newBaselineAggregations?.findIndex(
                (aggregation) => aggregation.column === baselineAggregationColumnName
              );
              if (newBaselineAggregations?.[newBaselineIdx]?.numericalAggregate) {
                newBaselineAggregations[newBaselineIdx].numericalAggregate = configToNumericalAggregate(newConfig);
              }
              onChangeMonitoringOptions({
                ...monitoringOptions,
                baselineAggregations: newBaselineAggregations,
              });
            } else if (baselineAggregation?.logicalType === LogicalType.CATEGORICAL) {
              const newBaselineAggregations = monitoringOptions?.baselineAggregations;
              const newBaselineIdx = newBaselineAggregations?.findIndex(
                (aggregation) => aggregation.column === baselineAggregationColumnName
              );
              if (newBaselineAggregations?.[newBaselineIdx]?.categoricalAggregate) {
                newBaselineAggregations[newBaselineIdx].categoricalAggregate = configToCategoricalAggregate(newConfig);
              }
              onChangeMonitoringOptions({
                ...monitoringOptions,
                baselineAggregations: newBaselineAggregations,
              });
            }
          }}
          readOnly={readOnly}
          editOnly={!readOnly && baselineAggregation?.logicalType === LogicalType.NUMERICAL}
          allowEmptyValues
          styles={{ root: { marginTop: 10, width: '100%' } }}
          directlyEditable
        />
      )}
    </>
  );
};

const Monitoring = ({
  monitoringOptions,
  onChangeMonitoringOptions,
  experimentId,
  disabled,
  nonUpdatableColumns,
  isExistingMonitoring,
  shouldFetchMetadata,
}: MonitoringProps) => {
  const { addToast } = useToast(),
    theme = useTheme(),
    classNames = useClassNames<IModelMetadataStyles, ClassNamesFromIStyles<IModelMetadataStyles>>(
      'modelMetadata',
      modelMetadataStyles(theme)
    ),
    [experimentMetadata, setExperimentMetadata] = React.useState<Metadata>(),
    [logicalTypeMap, setLogicalTypeMap] = React.useState<Record<string, LogicalType> | undefined>(),
    [columnSearchStr, setColumnSearchStr] = React.useState<{ [searchbox_key: string]: string }>(),
    // TODO: Move to context global to mlops.
    cloudPlatformDiscovery = useCloudPlatformDiscovery(),
    mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '',
    storageTransport = createConnectTransport({
      baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
    }),
    experimentClient = createClient(ExperimentService, storageTransport),
    // For some deployments created outside of the UI, there can be multiple output columns for single property.
    // UI handles this by modifiyng schema obtained from experiment metadata and replacing output columns with the corresponding ones from the deployment.
    // E.g.: these metadata "output_schema": {"jsonValue": "[{\"name\":\"Distance\",\"type\":\"NUMBER\"}]"}
    // are replaced by this "output_schema": {{"name":"Distance.0","type":"NUMBER"},{"name":"Distance.1","type":"NUMBER"}]
    columnsToReplace = React.useMemo(() => {
      const outputColumnsWithDotSuffix = monitoringOptions?.columns?.filter(
        (col) => col.isModelOutput && col.column.includes('.')
      );
      if (!outputColumnsWithDotSuffix?.length) return undefined;
      return outputColumnsWithDotSuffix?.reduce((acc, col) => {
        const colName = col.column.split('.')?.[0];
        if (Boolean(colName)) {
          acc[colName] = Array.isArray(acc[colName]) ? [...acc[colName], col] : [col];
        }
        return acc;
      }, {} as Record<string, ColumnWithProps[]>);
    }, [monitoringOptions?.columns]),
    getSchemasToLogicalTypeMap = (experimentMetadata: Metadata) => {
      const schemasToLogicalTypeMap: Record<string, LogicalType> = {};
      const isJson = experimentMetadata?.values?.['input_schema']?.value?.case === 'jsonValue';
      if (!isJson) {
        const message = 'No input schema found in the experiment metadata.';
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
        return undefined;
      }
      try {
        const jsonValueInput = experimentMetadata?.values?.['input_schema']?.value?.value;
        if (typeof jsonValueInput !== 'string') {
          throw new Error('Input schema value is not a string');
        }
        const inputSchema = JSON.parse(jsonValueInput);
        const jsonValueOutput = experimentMetadata?.values?.['output_schema']?.value?.value;
        if (typeof jsonValueOutput !== 'string') {
          throw new Error('Output schema value is not a string');
        }
        const outputSchema = JSON.parse(jsonValueOutput);
        [...inputSchema, ...outputSchema]?.forEach((field: any) => {
          const normalizedType = field.type.toUpperCase();
          schemasToLogicalTypeMap[field.name] =
            typeToLogicalTypeMap[normalizedType] ?? LogicalType.LOGICAL_TYPE_UNSPECIFIED;
        });
        const outputMinIdx: number = inputSchema.length ? inputSchema.length : 0;

        return { logicalTypeMap: schemasToLogicalTypeMap, outputMinIdx };
      } catch (error) {
        const message = `Failed to parse input schema: ${formatError(error)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
        return { logicalTypeMap: undefined, outputMinIdx: undefined };
      }
    },
    replaceOutputSchemaColumns = (metadata: any, replacements: Record<string, ColumnWithProps[]>) => {
      try {
        // Parse output schema (it's stored as a stringified JSON)
        const jsonValueOutput =
          metadata?.values?.['output_schema']?.value?.value || metadata?.values?.output_schema?.jsonValue; // Fallback depending on format it comes in.
        if (typeof jsonValueOutput !== 'string') {
          throw new Error('Output schema value is not a string');
        }
        const outputSchema = JSON.parse(jsonValueOutput);

        // New schema after replacements
        const newOutputSchema: any[] = [];

        for (const col of outputSchema) {
          if (replacements[col.name]) {
            // Replace with provided expanded columns
            for (const repl of replacements[col.name]) {
              newOutputSchema.push({
                name: repl.column,
                type: logicalTypeToTypeMap[repl.logicalType],
              });
            }
          } else {
            // Keep original column
            newOutputSchema.push(col);
          }
        }

        // Return new JSON with updated output schema
        return {
          ...metadata,
          values: {
            ...metadata.values,
            output_schema: {
              ...metadata.values.output_schema,
              jsonValue: JSON.stringify(newOutputSchema),
            },
          },
        };
      } catch {
        console.error('Failed to replace output schema columns');
        throw new Error('Failed to replace output schema columns');
      }
    },
    getExperimentMetadata = React.useCallback(
      async (experimentId: string, isNewMonitoring: boolean) => {
        try {
          const response = await experimentClient.getExperiment(
            new GetExperimentRequest({
              id: experimentId,
              responseMetadata: new KeySelection({
                pattern: ['input_schema', 'output_schema'],
              }),
            })
          );
          const experiment = response?.experiment;
          if (!experiment) console.error('No experiment found in the response.');
          const metadataWithModifiedOutputSchema = columnsToReplace
            ? replaceOutputSchemaColumns(experiment?.metadata, columnsToReplace)
            : experiment?.metadata;
          setExperimentMetadata(metadataWithModifiedOutputSchema);
          if (metadataWithModifiedOutputSchema) {
            const schemasTypes = getSchemasToLogicalTypeMap(metadataWithModifiedOutputSchema);
            const logicalTypeMap = schemasTypes?.logicalTypeMap;
            const outputMinIdx = schemasTypes?.outputMinIdx;
            setLogicalTypeMap(logicalTypeMap);
            if (logicalTypeMap) {
              onChangeMonitoringOptions({
                ...monitoringOptions,
                enabled: true,
                columns: Object.entries(logicalTypeMap).map(
                  ([column, logicalType], idx) =>
                    (!isNewMonitoring && monitoringOptions?.columns?.find((col) => col.column === column)) || {
                      column,
                      logicalType,
                      isModelOutput: outputMinIdx !== undefined ? idx >= outputMinIdx : undefined,
                      isColumnDisabled: isNewMonitoring ? undefined : true,
                    }
                ),
                baselineAggregations:
                  !isNewMonitoring && monitoringOptions?.baselineAggregations
                    ? monitoringOptions?.baselineAggregations
                    : Object.keys(logicalTypeMap).reduce((acc, columnName) => {
                        const logicalType = logicalTypeMap[columnName];
                        if (logicalType === LogicalType.NUMERICAL) {
                          acc.push({
                            column: columnName,
                            logicalType,
                            numericalAggregate: {
                              binEdges: [],
                              binCount: [],
                              mean: 0,
                              standardDeviation: 0,
                              min: 0,
                              max: 0,
                              sum: 0,
                            },
                          });
                        } else if (logicalType === LogicalType.CATEGORICAL) {
                          acc.push({
                            column: columnName,
                            logicalType,
                            categoricalAggregate: {
                              valueCounts: {},
                            },
                          });
                        }
                        return acc;
                      }, [] as BaselineAggregationFixed[]),
              });
            }
          }
        } catch (err) {
          const message = `Failed to fetch experiment metadata: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          setExperimentMetadata(undefined);
        }
      },
      [addToast, monitoringOptions, columnsToReplace]
    );

  React.useEffect(() => {
    if (experimentId && shouldFetchMetadata) getExperimentMetadata(experimentId, !isExistingMonitoring);
  }, [experimentId, isExistingMonitoring, shouldFetchMetadata]);

  return experimentMetadata && monitoringOptions ? (
    <>
      <TooltipLabel
        label="Kafka topic (optional)"
        tooltipText="Kafka topic to send scoring data to. If not set, default will be used."
      />
      <TextField
        styles={{ root: { marginBottom: 10, width: 280 } }}
        disabled={disabled}
        value={monitoringOptions?.kafkaTopic || ''}
        onChange={(_ev, newValue) => {
          onChangeMonitoringOptions({
            ...monitoringOptions,
            kafkaTopic: newValue,
          });
        }}
      />
      {Object.entries(experimentMetadata?.values)
        ?.filter((value) => value?.[1].value?.case === 'jsonValue')
        .map((value, schemaIdx) => {
          if (value?.[1].value?.case === 'jsonValue') {
            const parsedJson = getParsedJson(value?.[1].value?.value);
            const handleSelectColumns = (disable: boolean) => {
              const newColumns: ColumnWithProps[] = monitoringOptions?.columns || [];

              parsedJson?.forEach((field: { name: string; type: string }, columnIndex: number) => {
                let prevItemsCount = 0;

                if (schemaIdx > 0) {
                  prevItemsCount = Object.entries(experimentMetadata?.values)
                    ?.slice(0, schemaIdx)
                    ?.reduce((acc, [, value]) => {
                      if (value.value?.case === 'jsonValue') {
                        const parsed = getParsedJson(value.value.value);
                        return acc + (parsed?.length || 0);
                      }
                      return acc;
                    }, 0);
                }

                const index = prevItemsCount + columnIndex;
                newColumns[index] = {
                  column: field.name,
                  logicalType: logicalTypeMap?.[field.name] || LogicalType.LOGICAL_TYPE_UNSPECIFIED,
                  isModelOutput: value?.[0]?.includes('output_schema') ? true : undefined,
                  isColumnDisabled:
                    COLUMN_UPDATE_SUPPORTED && nonUpdatableColumns?.includes(field.name) ? false : disable,
                };
              });

              onChangeMonitoringOptions({
                ...monitoringOptions,
                columns: newColumns,
              });
            };

            if (Object.values(parsedJson?.[0] || {})?.length === 2 && parsedJson?.[0]?.name && parsedJson?.[0]?.type) {
              return (
                <div key={`monitoring-${value?.[0]}`}>
                  <h4 className={classNames.schemaTitle}>{value?.[0]?.replace('_', ' ')}</h4>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
                    <Search
                      onSearchTextChange={(text) => {
                        setColumnSearchStr((colSearch) =>
                          colSearch ? { ...colSearch, [value?.[0]]: text || '' } : { [value?.[0]]: text || '' }
                        );
                      }}
                      placeholder={'Search columns...'}
                      styles={{ root: { marginBottom: '10px', width: 280 } }}
                    />
                    {!disabled && (
                      <>
                        <Button
                          text={'Select all'}
                          onClick={() => handleSelectColumns(false)}
                          styles={buttonStylesStealth}
                        />
                        <Button
                          text={'Deselect all'}
                          onClick={() => handleSelectColumns(true)}
                          styles={buttonStylesStealth}
                        />
                      </>
                    )}
                  </div>
                  <div className={classNames.tableScrollWrapper}>
                    <table className={classNames.schemaTable}>
                      <colgroup>
                        <col style={{ width: 70 }} />
                        <col style={{ width: 160 }} />
                        <col style={{ width: 70 }} />
                        <col style={{ width: 160 }} />
                        <col style={{ width: 'auto' }} />
                      </colgroup>
                      <thead className={classNames.tableHeaderSticky}>
                        <tr>
                          <th>Monitor</th>
                          <th>Column</th>
                          <th>Type</th>
                          <th>
                            <TooltipLabel
                              label="Logical type"
                              tooltipText="Only NUMERICAL and CATEGORICAL types are monitored."
                              hasTopSpacing={false}
                              isBoldLabel
                            />
                          </th>
                          <th>Baseline aggregation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedJson
                          ? parsedJson?.map((field: { name: string; type: string }, columnIndex: number) => {
                              let prevItemsCount = 0;
                              if (schemaIdx > 0) {
                                prevItemsCount = Object.entries(experimentMetadata?.values)
                                  ?.slice(0, schemaIdx)
                                  ?.reduce((acc, [, value]) => {
                                    if (value.value?.case === 'jsonValue') {
                                      const parsed = getParsedJson(value.value.value);
                                      return acc + (parsed?.length || 0);
                                    }
                                    return acc;
                                  }, 0);
                              }
                              const index = prevItemsCount + columnIndex,
                                isOutputSchema = value?.[0]?.includes('output_schema'),
                                isMonitored = !monitoringOptions?.columns?.[index]?.isColumnDisabled || false,
                                isUpdatable = COLUMN_UPDATE_SUPPORTED
                                  ? !nonUpdatableColumns?.includes(field.name)
                                  : true,
                                baselineAggregation = monitoringOptions?.baselineAggregations?.find(
                                  (ba) => ba.column === monitoringOptions?.columns?.[index]?.column
                                ),
                                isColumnBaselineTypeSupported =
                                  baselineAggregation?.logicalType === LogicalType.NUMERICAL ||
                                  baselineAggregation?.logicalType === LogicalType.CATEGORICAL,
                                shouldDisplayColumn =
                                  !columnSearchStr?.[value?.[0]] ||
                                  field.name.toLowerCase().includes(columnSearchStr?.[value?.[0]]?.toLowerCase() || '');
                              return shouldDisplayColumn ? (
                                <tr
                                  key={`table-name-type-${index}`}
                                  style={{
                                    backgroundColor: isMonitored ? 'white' : '#f4f4f4', // TODO: Theme this.
                                    verticalAlign: 'top',
                                  }}
                                >
                                  <td>
                                    <Toggle
                                      label=""
                                      checked={isMonitored}
                                      disabled={disabled || !isUpdatable}
                                      styles={{ root: { marginTop: 8 } }}
                                      onChange={(_ev, checked) => {
                                        const newColumns: ColumnWithProps[] = monitoringOptions?.columns || [];
                                        newColumns[index] = {
                                          column: field.name,
                                          logicalType:
                                            logicalTypeMap?.[field.name] || LogicalType.LOGICAL_TYPE_UNSPECIFIED,
                                          isModelOutput: isOutputSchema ? true : undefined,
                                          isColumnDisabled: !checked ? true : undefined,
                                        };
                                        onChangeMonitoringOptions({
                                          ...monitoringOptions,
                                          columns: newColumns,
                                        });
                                      }}
                                    />
                                  </td>
                                  <td style={{ opacity: isMonitored ? 1 : 0.5 }}>
                                    <div style={{ marginTop: 7 }}>{field.name}</div>
                                  </td>
                                  <td style={{ opacity: isMonitored ? 1 : 0.5 }}>
                                    <div style={{ marginTop: 7 }}>{field.type}</div>
                                  </td>
                                  <td>
                                    {!disabled ? (
                                      <Dropdown
                                        options={logicalTypeDropdownOptions}
                                        selectedKey={
                                          isExistingMonitoring
                                            ? monitoringOptions?.columns?.[index]?.logicalType
                                            : logicalTypeMap?.[field.name]
                                        }
                                        disabled={!isMonitored || disabled || !isUpdatable}
                                        onChange={(_event, selectedOption) => {
                                          const selectedLogicalType = (selectedOption as LogicalTypeDropdownOption)
                                            .value;
                                          const oldColumns: ColumnWithProps[] = monitoringOptions?.columns || [];
                                          const oldAggregations: BaselineAggregationFixed[] =
                                            monitoringOptions?.baselineAggregations || [];

                                          const newColumns: ColumnWithProps[] = oldColumns.map((col, i) =>
                                            i === index
                                              ? {
                                                  column: field.name,
                                                  logicalType: selectedLogicalType,
                                                  isModelOutput: isOutputSchema ? true : undefined,
                                                }
                                              : col
                                          );

                                          const updatedAggregations = newColumns.reduce((acc, col) => {
                                            const existingAggregation = oldAggregations.find(
                                              (ba) => ba.column === col.column
                                            );

                                            if (col.logicalType === LogicalType.NUMERICAL) {
                                              acc.push(
                                                existingAggregation?.numericalAggregate
                                                  ? existingAggregation
                                                  : {
                                                      column: col.column,
                                                      logicalType: col.logicalType,
                                                      numericalAggregate: {
                                                        binEdges: [],
                                                        binCount: [],
                                                        mean: 0,
                                                        standardDeviation: 0,
                                                        min: 0,
                                                        max: 0,
                                                        sum: 0,
                                                      },
                                                    }
                                              );
                                            } else if (col.logicalType === LogicalType.CATEGORICAL) {
                                              acc.push(
                                                existingAggregation?.categoricalAggregate
                                                  ? existingAggregation
                                                  : {
                                                      column: col.column,
                                                      logicalType: col.logicalType,
                                                      categoricalAggregate: {
                                                        valueCounts: {},
                                                      },
                                                    }
                                              );
                                            }
                                            return acc;
                                          }, [] as BaselineAggregationFixed[]);

                                          onChangeMonitoringOptions({
                                            ...monitoringOptions,
                                            columns: newColumns,
                                            baselineAggregations: updatedAggregations,
                                          });

                                          setLogicalTypeMap((prevLogicalTypeMap) => ({
                                            ...prevLogicalTypeMap,
                                            [field.name]: selectedLogicalType,
                                          }));
                                        }}
                                      />
                                    ) : (
                                      <div style={{ marginTop: 7 }}>
                                        {isExistingMonitoring
                                          ? monitoringOptions?.columns?.[index]?.logicalType
                                          : logicalTypeMap?.[field.name]}
                                      </div>
                                    )}
                                  </td>
                                  <td style={{ opacity: isMonitored ? 1 : 0.5 }}>
                                    {isMonitored && isColumnBaselineTypeSupported ? (
                                      <ValueEditor
                                        onChangeMonitoringOptions={onChangeMonitoringOptions}
                                        readOnly={disabled || !isUpdatable}
                                        monitoringOptions={monitoringOptions}
                                        baselineAggregationColumnName={field.name}
                                      />
                                    ) : (
                                      ''
                                    )}
                                  </td>
                                </tr>
                              ) : null;
                            })
                          : null}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            } else {
              return null;
            }
          }
          return null;
        })}
    </>
  ) : (
    <p>No monitoring options to display.</p>
  );
};

export default Monitoring;
